import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faArrowLeft, faPrint } from '@fortawesome/free-solid-svg-icons';

const { rootUrl } = require('../../../../config/config');

const TransactionDetails = () => {
    const { plate_no, type, doc_no } = useParams();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState(null);
    const [carDetails, setCarDetails] = useState(null);
    const [driverDetails, setDriverDetails] = useState(null);
    const [ownerDetails, setOwnerDetails] = useState(null);
    const [transactionType, setTransactionType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTransactionDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('authToken') || 'default-token';

                // Fetch transaction
                const transResponse = await axios.get(
                    `${rootUrl}/cars/${plate_no}/pay/${type}/${doc_no}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!transResponse.data) {
                    setError('Transaction not found.');
                    return;
                }

                setTransaction(transResponse.data);

                // Fetch car details
                const carResponse = await axios.get(
                    `${rootUrl}/cars/${plate_no}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ).catch(() => null);

                if (carResponse?.data) {
                    setCarDetails(carResponse.data);

                    // Fetch driver details if driver_id exists
                    if (carResponse.data.driver_id) {
                        const driverResponse = await axios.get(
                            `${rootUrl}/drivers/id_no/${carResponse.data.driver_id}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        ).catch(() => {
                            // Try searching by id_no if direct ID fails
                            return axios.get(
                                `${rootUrl}/drivers?search=${carResponse.data.driver_id}&searchBy=id_no`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                        }).catch(() => null);

                        if (driverResponse?.data) {
                            const driverData = Array.isArray(driverResponse.data)
                                ? driverResponse.data[0]
                                : (driverResponse.data.data && Array.isArray(driverResponse.data.data)
                                    ? driverResponse.data.data[0]
                                    : driverResponse.data);
                            setDriverDetails(driverData);
                        }
                    }

                    // Fetch owner details if owner_code exists
                    if (carResponse.data.owner_code) {
                        const ownerResponse = await axios.get(
                            `${rootUrl}/owners/${carResponse.data.owner_code}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        ).catch(() => null);

                        if (ownerResponse?.data) {
                            const ownerData = ownerResponse.data.data || ownerResponse.data;
                            setOwnerDetails(ownerData);
                        }
                    }
                }

                // Fetch transaction type details
                const typeResponse = await axios.get(
                    `${rootUrl}/payments/types/car`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ).catch(() => null);

                if (typeResponse?.data) {
                    const foundType = typeResponse.data.find(t => t.type === type);
                    if (foundType) {
                        setTransactionType(foundType);
                    }
                }

            } catch (err) {
                console.error('Error fetching transaction details:', err);
                setError(err.response?.data?.error || 'Failed to fetch transaction details.');
            } finally {
                setLoading(false);
            }
        };

        if (plate_no && type && doc_no) {
            fetchTransactionDetails();
        }
    }, [plate_no, type, doc_no]);

    const formatMoney = (value) => {
        if (value === null || value === undefined || value === '-') return '-';
        return new Intl.NumberFormat('en-US').format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-center items-center h-64">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-[#FF5733]" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !transaction) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <h2 className="text-2xl font-bold text-[#2a2e30] mb-4">Transaction Not Found</h2>
                        <p className="text-gray-600 mb-6">{error || 'The requested transaction could not be found.'}</p>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-2 bg-[#FF5733] text-white font-semibold rounded-xl hover:bg-[#e64a2b] transition-all duration-200 flex items-center mx-auto"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Back
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={() => window.print()}
                            className="px-4 py-2 bg-[#FF5733] text-white font-semibold rounded-xl hover:bg-[#e64a2b] transition-all duration-200 flex items-center"
                        >
                            <FontAwesomeIcon icon={faPrint} className="mr-2" />
                            Print
                        </button>
                    </div>
                </div>

                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Transaction Details</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        {transactionType?.long_desc || type} - Document #{doc_no}
                    </p>
                </div>

                {/* Transaction Information */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <h2 className="text-2xl font-bold text-[#2a2e30] mb-6 flex items-center">
                        <div className="w-2 h-2 bg-[#FF5733] rounded-full mr-2"></div>
                        Transaction Information
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base font-bold text-[#2a2e30] mb-3">Basic Details</h3>
                            <div className="space-y-2.5 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Transaction ID:</span>
                                    <span className="font-mono font-semibold text-[#2a2e30]">{transaction.id}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Document Number:</span>
                                    <span className="font-mono font-semibold text-[#2a2e30]">{transaction.doc_no}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Type:</span>
                                    <span className="font-semibold text-[#2a2e30]">
                                        {transactionType?.long_desc || transaction.type}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Short Description:</span>
                                    <span className="font-semibold text-[#2a2e30]">{transaction.short_desc || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Date:</span>
                                    <span className="font-semibold text-[#2a2e30]">{formatDate(transaction.date)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Week:</span>
                                    <span className="font-semibold text-[#2a2e30]">{transaction.week || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base font-bold text-[#2a2e30] mb-3">Financial Details</h3>
                            <div className="space-y-2.5 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Amount:</span>
                                    <span className="font-mono font-bold text-lg text-[#2a2e30]">
                                        {formatMoney(transaction.amount)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Debit:</span>
                                    <span className="font-mono font-semibold text-red-600">
                                        {formatMoney(transaction.debit)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Credit:</span>
                                    <span className="font-mono font-semibold text-green-600">
                                        {formatMoney(transaction.credit)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Post Balance:</span>
                                    <span className="font-mono font-semibold text-[#2a2e30]">
                                        {formatMoney(transaction.post_balance)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* References and Notes */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base font-bold text-[#2a2e30] mb-3">References</h3>
                            <div className="space-y-2.5 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Your Reference:</span>
                                    <span className="font-mono font-semibold text-[#2a2e30]">
                                        {transaction.your_ref || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Our Reference:</span>
                                    <span className="font-mono font-semibold text-[#2a2e30]">
                                        {transaction.our_ref || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base font-bold text-[#2a2e30] mb-3">Audit Information</h3>
                            <div className="space-y-2.5 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Input By:</span>
                                    <span className="font-semibold text-[#2a2e30]">
                                        {transaction.input_by || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700 font-medium">Input Date:</span>
                                    <span className="font-semibold text-[#2a2e30]">
                                        {formatDateTime(transaction.input_date)}
                                    </span>
                                </div>
                                {transaction.edit_by && (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Edit By:</span>
                                            <span className="font-semibold text-[#2a2e30]">
                                                {transaction.edit_by}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Edit Date:</span>
                                            <span className="font-semibold text-[#2a2e30]">
                                                {formatDateTime(transaction.edit_date)}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {transaction.notes && (
                        <div className="mt-6 bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base font-bold text-[#2a2e30] mb-3">Notes</h3>
                            <p className="text-sm text-[#2a2e30] whitespace-pre-wrap">{transaction.notes}</p>
                        </div>
                    )}
                </div>

                {/* Car Information */}
                {carDetails && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <h2 className="text-2xl font-bold text-[#2a2e30] mb-6 flex items-center">
                            <div className="w-2 h-2 bg-[#FF5733] rounded-full mr-2"></div>
                            Car Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Vehicle Details</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Plate Number:</span>
                                        <Link
                                            to={`/cars/${carDetails.plate_no}`}
                                            className="font-bold text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                                        >
                                            {carDetails.plate_no}
                                        </Link>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Make:</span>
                                        <span className="font-semibold text-[#2a2e30]">{carDetails.make || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Model:</span>
                                        <span className="font-semibold text-[#2a2e30]">{carDetails.model || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Year:</span>
                                        <span className="font-semibold text-[#2a2e30]">{carDetails.year || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Owner Information</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Owner Code:</span>
                                        <Link
                                            to="/owners"
                                            className="font-semibold text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                                        >
                                            {carDetails.owner_code || 'N/A'}
                                        </Link>
                                    </div>
                                    {ownerDetails?.name && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">Owner Name:</span>
                                            <span className="font-semibold text-[#2a2e30]">{ownerDetails.name}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Financial Status</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Balance:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {formatMoney(carDetails.bal)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Balance B/F:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {formatMoney(carDetails.bal_bf)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Weekly Amount:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {formatMoney(carDetails.weekly_amount)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Driver Information */}
                {driverDetails && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <h2 className="text-2xl font-bold text-[#2a2e30] mb-6 flex items-center">
                            <div className="w-2 h-2 bg-[#FF5733] rounded-full mr-2"></div>
                            Driver Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">Personal Details</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Name:</span>
                                        <Link
                                            to={driverDetails.id ? `/drivers?id=${driverDetails.id}` : "/drivers"}
                                            className="font-semibold text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                                        >
                                            {driverDetails.name || 'N/A'}
                                        </Link>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">ID Number:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {driverDetails.id_no || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">Phone:</span>
                                        <span className="font-semibold text-[#2a2e30]">
                                            {driverDetails.phone || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-base font-bold text-[#2a2e30] mb-3">License Information</h3>
                                <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-700 font-medium">License Number:</span>
                                        <span className="font-mono font-semibold text-[#2a2e30]">
                                            {driverDetails.license_no || 'N/A'}
                                        </span>
                                    </div>
                                    {driverDetails.license_expiry && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-700 font-medium">License Expiry:</span>
                                            <span className={`font-semibold ${new Date(driverDetails.license_expiry) < new Date()
                                                ? 'text-red-600 font-bold'
                                                : new Date(driverDetails.license_expiry) <= new Date(new Date().setMonth(new Date().getMonth() + 1))
                                                    ? 'text-yellow-600 font-bold'
                                                    : 'text-[#2a2e30]'
                                                }`}>
                                                {formatDate(driverDetails.license_expiry)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransactionDetails;

